import { GuaranteeForm } from "@app/guaranteeForm";
import { DeepPartial, FormProvider, useForm, useWatch } from "react-hook-form";
import { Button } from "antd";
import { useSaveOrder } from "./useSaveOrder";
import { GuaranteeFormContext } from "@app/guranteeFormWrapper";
import { BgFormType } from "@services/guarantees/types";
import { useResetForm } from "@app/hooks/useResetForm";
import { useEffect, useState } from "react";
import { bxGetInnForCurrentDeal } from "@lib/bx24";
import BankSelect from "./BankSelect";
import { pluralize } from "@utils/pluralize";
import { BgFormInput, BgFormOutput, BgFormSchema } from "@utils/guaranteeForm/formShema";
import { zodResolver } from "@hookform/resolvers/zod";
import { LOT_MODEL } from "@utils/purchaseLotModels";
import { useBankSelect } from "./useBankSelect";

export const GuaranteeStartFormWrapper = ({ onCreated }) => {
  const methods = useForm<BgFormInput, unknown, BgFormOutput>({
    resolver: zodResolver(BgFormSchema),
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      principal: { inn: null },
      guarantee: { type: null, startDate: null, endDate: null, amount: null },
      purchase: {
        purchaseNumber: null,
        marketPlace: null,
        auctionType: null,
        tenderType: null,
        law: null,
        tenderDate: null,
        purchaseLink: null,
        purchaseSubject: null,
        lots: [LOT_MODEL],
      },
      requestedBanks: [],
    } satisfies DeepPartial<BgFormInput>,
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
  } = methods;

  const { banks, selectedBanks, onSelectBank } = useBankSelect({ methods });

  const { handleSave, saving } = useSaveOrder();
  const { resetToEmpty } = useResetForm<BgFormType>();

  const [loading, setLoading] = useState(true);

  const [principal] = useWatch({
    control: methods.control,
    name: ["principal"],
  });

  useEffect(() => {
    (async () => {
      try {
        const inn = await bxGetInnForCurrentDeal();

        if (!inn) {
          setLoading(false)
          return;
        }

        methods.reset({
          principal: { inn }
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false)
      }
    })();
  }, []);

  const isSubmitDisabled = !isValid || !!Object.keys(errors).length || !selectedBanks.size;

  const onSave = handleSubmit(async (data) => {
    try {
      if (selectedBanks.size > 0) {
        data.requestedBanks = Array.from(selectedBanks);
      }

      const newOrderId = await handleSave(data);
      resetToEmpty(methods.getValues, methods.reset);
      onCreated(newOrderId)
    } catch (e) {
      console.error(e);
    }
  });

  if (loading) return <div className="row muted">Идёт загрузка...</div>;
  if (!principal?.inn) {
    return <div className="row err">Заполните данные компании и обновите страницу</div>
  }

  return (
    <FormProvider {...methods}>
      <GuaranteeFormContext.Provider
        value={{
          formDisabled: false,
          prefix: "",
          isStartForm: true,
          canEditPurchase: true,
        }}
      >
        <BankSelect banks={banks} selectedBanks={selectedBanks} onSelect={onSelectBank} />
        <fieldset>
          <GuaranteeForm />
        </fieldset>
        <Button type="primary" onClick={onSave} loading={saving} disabled={isSubmitDisabled}>
          Отправить заявку в {selectedBanks.size} {pluralize(selectedBanks.size, ['банк', 'банка', 'банков'])}
        </Button>
      </GuaranteeFormContext.Provider>
    </FormProvider>
  );
};
